import { connect } from 'react-redux';
import React from 'react';
import { formValueSelector, reduxForm, reset } from 'redux-form';
import { Box, Card, CardContent, Grid, InputLabel } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { result, size, get, find } from 'lodash';
import PropTypes from 'prop-types';
import ChargingSessionsTab from './ChargingSessionsTab';
import FaultsTab from './FaultsTab';
import ChargerDetailsToolbar from './ChargerDetailsToolbar';
import ActivitiesTab from './ActivitiesTab';
import Loader from '../../../common/components/Loader/Loader';
import { Tab, Tabs } from '../../../common/components/Tabs/Tabs';
import Select from '../../../common/components/Select/Select';
import { isMobile } from '../../../common/services/isMobile';
import { ChargerConfiguration } from './ChargerConfiguration';
import { ChargerPackages } from './ChargerPackages';
import CommonNavigationPrompt from '../../../common/components/NavigationPrompt/component';
import TasksTab from './TasksTab';
import MappedLabel from '../../../common/components/MappedLabel/MappedLabel';
import hasAccess, { viewChargerConfigsId, viewChargerTaskAccessId, viewChargerTelemetry } from '../../../common/services/HasAccess';
import {
  getExportActivitiesLimit,
  getExportChargerSessionsLimit,
  getExportFailuresLimit,
  isChargerTelemetryAvailable,
  showChargerTasks,
} from '../../../common/components/appConfig/selector';
import { MenuItem } from '../../../common/components/MenuItem/MenuItem';
import TabWithSubItems from '../../PlazaView/components/TabWithSubItems';
import { getDiagnosticAvailable } from '../selectors';
import Telemetry from './Telemetry';
import uiLabelsPaths from '../../../common/services/uiFieldLabelsDictionary';

export const FORM_NAME = 'chargerDetails';
const noData = <div className="no-data-label">No Data...</div>;

function ChargerDetailsForm(props) {
  const {
    handleSubmit,
    data,
    currentChargerData,
    ifModal,
    activeTabValue,
    isMobileMenuOpened,
    onMobileMenuOpen,
    onMobileMenuClose,
    selectTab,
  } = props;

  const chargerUuid = get(currentChargerData, 'c_uuid');

  const onTabChange = (event, tab) => {
    if (tab === 'telemetry') {
      return;
    }

    selectTab(tab);
  };

  const handleMenuItemClick = (tab) => {
    selectTab(tab);
    if (isMobile && isMobileMenuOpened) {
      onMobileMenuClose();
    }
  };

  const mobileTabChange = (event) => {
    selectTab(event.target.value);
  };

  const renderField = (field, index) => {
    if (!field.component) {
      return null;
    }

    return (
      <Box key={field.name || index} sx={{ p: 1 }}>
        {field.component(field, props)}
        {field.autoActivated && (
          <InputLabel className="label--auto-activated">
            <Warning className="icon" /> System generated
          </InputLabel>
        )}
      </Box>
    );
  };

  const renderFieldSet = (fields) =>
    size(fields) ? fields.map((field, index) => (field.show !== false ? renderField(field, index) : null)).filter((field) => field) : [];

  const renderSectionsAsGrid = (item, withShadow = false) => {
    const tabInner = [];
    const gridItemProps = {};

    if (size(item.sections) <= 2) {
      gridItemProps.xs = 6;
    } else {
      gridItemProps.lg = true;
    }

    for (const section of item.sections) {
      const sectionInner = renderFieldSet(section.fields);
      const optClassName = result(item, 'className', '');
      const sectionClassName = `charger-details-section s-${size(item.sections)} ${optClassName}`;

      tabInner.push(
        <Grid key={section.value} item style={{ minWidth: 300 }} {...gridItemProps}>
          <Card style={{ height: '100%' }} className={sectionClassName}>
            <CardContent className={withShadow ? 'items-with-shadow' : ''}>{sectionInner}</CardContent>
          </Card>
        </Grid>,
      );
    }

    return (
      <div key={`charger-${activeTabValue}-tab`} className={`charger-${activeTabValue}-tab`}>
        <Grid container justifyContent="center" spacing={2} style={{ padding: 16, backgroundColor: '#fff' }}>
          {tabInner.length ? tabInner : noData}
        </Grid>
      </div>
    );
  };

  const renderConfiguration = () => (
    <ChargerConfiguration
      configuration={props.configuration}
      fieldsPathMap={props.fieldsPathMap}
      config={props.config}
      onSubmit={props.setConfigurationConfirmationDialogOpen}
      closeDialog={props.setConfigurationConfirmationDialogClosed}
      isDialogOpen={props.isConfigurationConfirmationDialogOpen}
      isReadOnly={props.readOnly}
      configurationFieldsRules={props.configurationFieldsRules}
      updateChargerConfiguration={props.updateChargerConfiguration}
      lockChargerConfiguration={props.lockChargerConfiguration}
      currentCharger={props.currentCharger}
      submitError={props.configurationParametersSubmitError}
      isFetching={props.isConfigurationSubmitFetching}
      showLockedPopup={props.showConfigurationLockedPopup}
      toggleLockedPopup={props.toggleShowConfigurationLockedPopup}
      resetField={props.resetField}
      lockInterval={props.lockInterval}
    />
  );

  const renderPackages = (chargerUuid) => <ChargerPackages chargerUuid={chargerUuid} />;

  const renderDefaultTabs = (item) => <Tab key={item.label} label={item.label} value={item.value} />;

  const renderTabWithSubmenu = (item, isFlat, modal) => (
    <TabWithSubItems
      menuItems={item.tabItems}
      menuItemClick={handleMenuItemClick}
      label={item.label}
      value={item.value}
      isFlat={isFlat}
      key={item.value}
      modal={modal}
    />
  );

  const renderTabs = (data, isMobile, ifModal, currentCharger) => {
    const tabs = data.map((item) =>
      !item.hidden
        ? item.tabItems && item.tabItems.length > 0
          ? renderTabWithSubmenu(item, false, ifModal)
          : renderDefaultTabs(item)
        : null,
    );

    const chargerUuid = get(currentCharger, 'c_uuid');

    return (
      <>
        {!isMobile && (
          <Box className="charger-details-tabs" display="flex" justifyContent="center" width="100%" bgcolor="rgba(39, 94, 99, 0.1)">
            <Tabs variant="scrollable" centered={false} value={props.activeTabValue} onChange={onTabChange}>
              {tabs}
              <Tab label={<MappedLabel reserve="Faults">chargerDetails.faultsTabLabel</MappedLabel>} value="faults" />
              <Tab
                label={<MappedLabel reserve="Charging Sessions">chargerDetails.chargingSessionsTabLabel</MappedLabel>}
                value="sessions"
              />
              <Tab label={<MappedLabel reserve="Activities">chargerDetails.activitiesTabLabel</MappedLabel>} value="activities" />
              {props.showChargerTasks && hasAccess(viewChargerTaskAccessId, props.userPermissions) && (
                <Tab label={<MappedLabel reserve="Tasks">chargerDetails.taskTabLabel</MappedLabel>} value="tasks" />
              )}
            </Tabs>
          </Box>
        )}
        {props.activeTabValue === 'details' && renderSectionsAsGrid(find(data, { value: 'details' }), true)}
        {(props.activeTabValue === 'telemetry' || props.activeTabValue === 'telemetry/realtime') &&
          props.telemetryIsAvailable &&
          hasAccess(viewChargerTelemetry, props.userPermissions) && (
            <Telemetry
              chargerId={chargerUuid}
              demo={window.location.search.includes('demo')}
              realTime={props.activeTabValue === 'telemetry/realtime'}
            />
          )}
        {props.activeTabValue === 'configuration' && hasAccess(viewChargerConfigsId, props.userPermissions) && renderConfiguration()}
        {props.activeTabValue === 'packages' && renderPackages(chargerUuid)}
        {props.activeTabValue === 'state' && renderSectionsAsGrid(find(data, { value: 'state' }))}
        {props.activeTabValue === 'faults' && (
          <FaultsTab
            exportLimit={props.faultsExportLimit}
            onGetDataAction={(...args) => props.getSSPData(chargerUuid, ...args)}
            charger={currentCharger}
            isMobile={isMobile}
            isModal={ifModal}
            onExport={props.onChargerDetailsTabExport}
            config={props.config}
          />
        )}
        {props.activeTabValue === 'sessions' && (
          <ChargingSessionsTab
            exportLimit={props.chargerSessionsExportLimit}
            onGetDataAction={(...args) => props.getSSPData(chargerUuid, ...args)}
            charger={currentCharger}
            isMobile={isMobile}
            isModal={ifModal}
            onExport={props.onChargerDetailsTabExport}
            config={props.config}
          />
        )}
        {props.activeTabValue === 'activities' && (
          <ActivitiesTab
            exportLimit={props.activitiesExportLimit}
            onGetDataAction={(...args) => props.getSSPData(chargerUuid, ...args)}
            config={props.config}
            onExport={props.onChargerDetailsTabExport}
            isMobile={isMobile}
            isModal={ifModal}
            charger={currentCharger}
          />
        )}
        {props.showChargerTasks && props.activeTabValue === 'tasks' && <TasksTab charger={currentCharger} config={props.config} />}
      </>
    );
  };

  const tabsContent = size(data) && chargerUuid && !props.isFetching ? renderTabs(data, isMobile, ifModal, currentChargerData) : <Loader />;

  const mobileMenuItems = data
    .filter((item) => !item.hidden)
    .map((item) =>
      item.tabItems && item.tabItems.length > 0 ? (
        renderTabWithSubmenu(item, true, item.label)
      ) : (
        <MenuItem key={item.value} value={item.value}>
          {item.label}
        </MenuItem>
      ),
    );

  const mobileTabMenuItems = [
    ...mobileMenuItems,
    <MenuItem key="faults" value="faults">
      <MappedLabel reserve="Faults">chargerDetails.faultsTabLabel</MappedLabel>
    </MenuItem>,
    <MenuItem key="charging-sessions" id="charging-sessions" value="sessions">
      <MappedLabel reserve="Charging Sessions">chargerDetails.chargingSessionsTabLabel</MappedLabel>
    </MenuItem>,
    <MenuItem key="activities" value="activities">
      <MappedLabel reserve="Activities">chargerDetails.activitiesTabLabel</MappedLabel>
    </MenuItem>,
    props.showChargerTasks && hasAccess(viewChargerTaskAccessId, props.userPermissions) && (
      <MenuItem key="tasks" value="tasks">
        <MappedLabel reserve="Tasks">chargerDetails.tasksTabLabel</MappedLabel>
      </MenuItem>
    ),
  ];

  const mobileTabMenu = isMobile && (
    <Select
      open={isMobileMenuOpened}
      onClose={onMobileMenuClose}
      onOpen={onMobileMenuOpen}
      className="charger-details-tabs tab-select select-big-icon"
      style={{ width: 260 }}
      value={activeTabValue}
      onChange={mobileTabChange}
      renderValue={(val) => {
        const el = find(mobileTabMenuItems, (item) => item.key === val);
        const reservedLabel = get(el, 'props.children.props.reserve');
        const childrenLabel = get(el, 'props.children');
        const simpleLabel = get(el, 'props.label');

        return reservedLabel || simpleLabel || childrenLabel || val;
      }}
    >
      {mobileTabMenuItems}
    </Select>
  );

  return (
    <>
      {!ifModal && <ChargerDetailsToolbar {...props} onClose={() => props.backButtonHandler()} />}
      <form id="createEditChargerForm" noValidate onSubmit={handleSubmit}>
        {mobileTabMenu}
        {tabsContent}
      </form>
      <CommonNavigationPrompt
        when={props.showExitDialog}
        customOnConfirm={() => {
          props.dispatch(reset(FORM_NAME));
        }}
      />
    </>
  );
}

ChargerDetailsForm.propTypes = {
  c_country: PropTypes.string,
  change: PropTypes.func,
  config: PropTypes.object,
  currentChargerData: PropTypes.object,
  data: PropTypes.array,
  dictionaries: PropTypes.object,
  getOldSSPData: PropTypes.func,
  getSSPData: PropTypes.func,
  handleSubmit: PropTypes.func,
  ifModal: PropTypes.bool,
  selectTab: PropTypes.func,
  touch: PropTypes.func,
  userPermissions: PropTypes.array,
  showChargerTasks: PropTypes.bool,
  activitiesExportLimit: PropTypes.number,
  telemetryIsAvailable: PropTypes.bool,
  openExitDialog: PropTypes.bool,
};

const ChargerDetailsFormWrapper = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(ChargerDetailsForm);

const selector = formValueSelector(FORM_NAME);

export default connect((state) => ({
  telemetryIsAvailable: isChargerTelemetryAvailable(state),
  faultsExportLimit: getExportFailuresLimit(state),
  activitiesExportLimit: getExportActivitiesLimit(state),
  chargerSessionsExportLimit: getExportChargerSessionsLimit(state),
  showChargerTasks: showChargerTasks(state),
  userPermissions: get(state, 'login.user.currentPermissions'),
  p_type: selector(state, uiLabelsPaths.p_type),
  c_country: selector(state, 'c_identity.c_country'),
  c_latitude: selector(state, 'c_identity.c_latitude'),
  c_longitude: selector(state, 'c_identity.c_longitude'),
  c_address: selector(state, 'c_identity.c_address'),
  isDiagnosticsAvailable: getDiagnosticAvailable(state),
}))(ChargerDetailsFormWrapper);
